import { useCallback } from 'react'
import styled, { css } from 'styled-components'
import BodyText, { BodyTextMedium } from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import noop from 'lib/function/noop'
import { rem } from 'polished'

interface TourOffer2027TravelBannerProps {
  className?: string
  eyebrow: string
  title: string
  description: string
  termsLabel?: string
  termsHref?: string
  onTermsClick?: () => void
}

const BannerStage = styled.section`
  position: relative;
  display: flex;
  justify-content: center;
  background-color: rgb(54, 58, 69);
  padding: ${rem(24)} 0 0;

  @media (min-width: 768px) {
    justify-content: flex-end;
  }
`

const BannerCard = styled.div`
  position: relative;
  margin: 0;
  max-width: ${rem(780)};
  border: 1px solid #16e5d0;
  border-radius: ${rem(20)};
  background: linear-gradient(180deg, #d9fff5 0%, #d0fff0 100%);
  box-shadow:
    0 0 0 1px rgba(22, 229, 208, 0.2),
    0 ${rem(20)} ${rem(48)} rgba(15, 45, 118, 0.2);
  padding: ${rem(56)} ${rem(28)} ${rem(28)};

  @media (min-width: 768px) {
    padding: ${rem(64)} ${rem(48)} ${rem(32)};
  }
`

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: max-content;
  max-width: calc(100% - ${rem(32)});
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: ${rem(4)} ${rem(14)};
  background: linear-gradient(180deg, #22f0c8 0%, #12dcb8 100%);
  box-shadow: 0 ${rem(8)} ${rem(24)} rgba(18, 220, 184, 0.35);
  color: #12312e;
  font-size: ${rem(18)};
  font-weight: 400;
  line-height: 1;
  text-align: center;
  white-space: nowrap;
`

const CardContent = styled.div`
  text-align: center;
`

const BannerDescription = styled(BodyText)`
  margin: ${rem(12)} auto 0;
  max-width: ${rem(520)};
`

const TermsAnchorStyles = css`
  ${BodyTextMedium}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: ${rem(12)};
  padding: 0;
  border: 0;
  background: transparent;
  color: #324867;
  font-weight: 600;
  line-height: 1.2;
  text-decoration: underline;
  cursor: pointer;
`

const TermsAnchor = styled.a`
  ${TermsAnchorStyles}
`

const TermsButton = styled.button`
  ${TermsAnchorStyles}
`

function TourOffer2027TravelBanner(props: TourOffer2027TravelBannerProps) {
  const {
    className,
    eyebrow,
    title,
    description,
    termsLabel,
    termsHref,
    onTermsClick = noop,
  } = props

  const handleTermsClick = useCallback(() => {
    onTermsClick()
  }, [onTermsClick])
  let termsAction = null

  if (termsLabel) {
    if (termsHref) {
      termsAction = <TermsAnchor href={termsHref} onClick={handleTermsClick}>
        {termsLabel}
      </TermsAnchor>
    } else {
      termsAction = <TermsButton type="button" onClick={handleTermsClick}>
        {termsLabel}
      </TermsButton>
    }
  }

  return (
    <BannerStage className={className}>
      <BannerCard>
        <Eyebrow>{eyebrow}</Eyebrow>
        <CardContent>
          <Heading variant="heading4" align="center">
            {title}
          </Heading>
          <BannerDescription variant="medium">
            {description}
          </BannerDescription>
          {termsAction}
        </CardContent>
      </BannerCard>
    </BannerStage>
  )
}

export type { TourOffer2027TravelBannerProps }
export default TourOffer2027TravelBanner
